/* eslint-disable no-console */
/**
 * Preview Pane Component
 *
 * Shows the generated image preview with Accept/Reject actions.
 * Appears within the panel after an action generates a result.
 */

import { cn } from '@pray/shared/utils/styles';

import type { PreviewPaneProps } from '../types';

import styles from './PreviewPane.module.scss';

// Check icon for accept
function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// X icon for reject
function XIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M18 6L6 18M6 6l12 12"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Download icon
function DownloadIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Retry icon
function RetryIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M1 4v6h6M23 20v-6h-6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M20.49 9A9 9 0 005.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 013.51 15"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function PreviewPane({
  result,
  recommendation,
  onAccept,
  onReject,
  onDownload,
  onRetry,
  isAccepting = false,
  className,
}: PreviewPaneProps) {
  const { imageUrl } = result;
  // Use imageKey from context as the target field
  const targetField = (recommendation.context.imageKey as string) || 'image';

  // Format target field name for display
  const formatFieldName = (field: string): string => {
    return field
      .replace(/_/g, ' ')
      .replace(/url$/i, '')
      .trim()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const targetFieldDisplay = formatFieldName(targetField);

  return (
    <div className={cn(styles.container, className)}>
      <div className={styles.header}>
        <h3 className={styles.title}>Preview Result</h3>
        <p className={styles.subtitle}>
          Will be applied to: <strong>{targetFieldDisplay}</strong>
        </p>
      </div>

      <div className={styles.imageContainer}>
        <img src={imageUrl} alt={`Generated ${recommendation.title}`} className={styles.image} />
      </div>

      <div className={styles.actions}>
        {/* Secondary actions */}
        <div className={styles.secondaryActions}>
          {onDownload && (
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={onDownload}
              aria-label="Download image"
              title="Download"
            >
              <DownloadIcon />
            </button>
          )}
          {onRetry && (
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={onRetry}
              aria-label="Regenerate image"
              title="Regenerate"
            >
              <RetryIcon />
            </button>
          )}
        </div>

        {/* Primary actions */}
        <div className={styles.primaryActions}>
          <button
            type="button"
            className={cn(styles.button, styles.rejectButton)}
            onClick={() => {
              console.log('[PreviewPane] Discard button clicked, isAccepting:', isAccepting);
              if (onReject) onReject();
            }}
            disabled={isAccepting}
            aria-label="Reject and discard"
          >
            <XIcon />
            <span>Discard</span>
          </button>

          <button
            type="button"
            className={cn(styles.button, styles.acceptButton)}
            onClick={() => {
              console.log('[PreviewPane] Apply button clicked, isAccepting:', isAccepting, 'onAccept:', !!onAccept);
              if (onAccept) onAccept();
            }}
            disabled={isAccepting}
            aria-label={`Apply to ${targetFieldDisplay}`}
          >
            <CheckIcon />
            <span>{isAccepting ? 'Saving...' : 'Apply'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default PreviewPane;
